import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Cylinder,
  DraftingCompass,
  Droplets,
  Factory,
  FlaskConical,
  Fuel,
  Landmark,
  Server,
  UtensilsCrossed,
  Wind,
  Zap,
} from "lucide-react";

const INDUSTRY_ICONS = {
  "chemical-petrochemical": FlaskConical,
  manufacturing: Factory,
  "oil-gas": Fuel,
  "lng-facilities": Cylinder,
  "energy-power": Zap,
  "data-centers": Server,
  "water-wastewater": Droplets,
  "food-beverage": UtensilsCrossed,
  "renewable-energy": Wind,
  "commercial-industrial": Building2,
  epc: DraftingCompass,
  government: Landmark,
};

export function ServiceCard({ service, testId }) {
  return (
    <Link
      to={`/services/${service.slug}`}
      data-testid={testId || `service-card-${service.slug}`}
      className="card-zoom card-line group relative block overflow-hidden bg-gig-navy"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={service.image.replace(".webp", "-900.webp")}
          alt={service.imageAlt}
          className="zoom-img absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          width="900"
          height="675"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gig-black/95 via-gig-black/35 to-gig-black/10 group-hover:via-gig-black/20 transition-colors duration-500" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
          <h3 className="font-condensed text-2xl font-bold uppercase leading-[0.95] text-white">
            {service.name}
          </h3>
          <span className="mb-1 flex h-9 w-9 flex-shrink-0 items-center justify-center border border-white/40 text-white transition-all duration-300 group-hover:border-gig-red group-hover:bg-gig-red">
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function IndustryCard({ industry, featured = false }) {
  const Icon = INDUSTRY_ICONS[industry.slug] || Factory;
  return (
    <Link
      to={`/industries/${industry.slug}`}
      data-testid={`industry-card-${industry.slug}`}
      className={`card-zoom group relative block overflow-hidden bg-white border border-gig-steel/15 transition-all duration-300 hover:border-gig-red/50 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(10,12,15,0.12)] ${
        featured ? "md:col-span-2 ring-1 ring-gig-red/30" : ""
      }`}
    >
      <div className={`relative overflow-hidden ${featured ? "aspect-[16/8]" : "aspect-[16/9]"}`}>
        <img
          src={industry.image.replace(".webp", "-900.webp")}
          alt={industry.imageAlt}
          className="zoom-img absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          width="900"
          height="506"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gig-black/60 to-transparent" aria-hidden="true" />
        <span className="absolute bottom-4 left-4 flex h-10 w-10 items-center justify-center bg-gig-red text-white transition-transform duration-300 group-hover:-translate-y-1" aria-hidden="true">
          <Icon className="h-5 w-5 text-white" strokeWidth={1.75} />
        </span>
        {featured && (
          <span className="absolute left-4 top-4 bg-gig-redDeep px-2.5 py-1 font-condensed text-xs font-bold uppercase tracking-[0.16em] text-white">
            Growth Market
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 p-4">
        <h3 className={`font-condensed font-bold uppercase leading-tight text-gig-black group-hover:text-gig-red transition-colors ${featured ? "text-xl md:text-2xl" : "text-lg"}`}>
          {industry.name}
        </h3>
        <ArrowRight className="h-4 w-4 flex-shrink-0 text-gig-steel transition-all duration-300 group-hover:translate-x-1 group-hover:text-gig-red" aria-hidden="true" />
      </div>
    </Link>
  );
}
